import React from "react";

// Panel 3's visual: a mini "Entries by Participants" table — the real
// product view (players as rows, rounds as columns, picks with a
// win/loss result), not an abstraction of it. Self-contained SVG, no
// dependence on other components' markup/styles. Built in panel 2's
// visual language: banded dark rows, a header row, small team-colour
// shirt chips, the same grammar as panel 2's Round 1/Round 2 picker.
//
// v4 (replaces v3's bar stack): 4 fictional-but-plausible player rows x
// 3 round columns. A player's cells after their loss are left empty —
// the emptiness itself tells the elimination story — rather than
// dimming the whole row, so the moment of elimination (the loss cell
// itself) stays fully readable.
//
// Two money annotations, which is what makes this panel 3 and not a
// repeat of panel 2: the R3 column gets a yellow tint + yellow header
// text, and Player 3's row (the survivor) gets a trophy mark plus a
// subtle yellow row outline. Those two visual treatments carry the
// meaning on their own — v4 also had "Final players / share a bonus"
// and "Last one / takes the rest" captions under the table; v5 (per
// review) removed them as redundant with the panel copy above and the
// "pot is always won" banner below, and dropped the `compact` cell
// variant now that the default (icon + team code + result) is final.
//
// With the captions gone, the table's own content is shorter than the
// 216-240px every image-bearing panel-3 slot has used, so the viewBox
// is sized to the table's actual footprint (not an arbitrary taller
// canvas) and left to the SVG's default preserveAspectRatio ("xMidYMid
// meet") to center it inside the card — that reads as the table sitting
// comfortably with even breathing room, rather than a leftover gap
// where the captions used to be.
const TEAM_COLORS = {
  ARS: "#C8102E",
  CHE: "#034694",
  TOT: "#132257",
  LIV: "#C8102E",
  MCI: "#6CADDF",
  EVE: "#00A650",
  WOL: "#F36C21",
  WHU: "#7A263A",
  BOU: "#DA291C",
};

// Player 1: wins R1, loses R2 - R3 empty.
// Player 2: loses R1 immediately - R2/R3 empty.
// Player 3: wins all three rounds - the survivor, gets the trophy.
// Player 4: wins R1/R2, loses R3 - fully played out, no trophy.
const ROWS = [
  { player: "Player 1", picks: ["ARS", "CHE", null], loseAt: 1 },
  { player: "Player 2", picks: ["TOT", null, null], loseAt: 0 },
  { player: "Player 3", picks: ["LIV", "MCI", "EVE"], winner: true },
  { player: "Player 4", picks: ["WOL", "WHU", "BOU"], loseAt: 2 },
];

const COL_X = [66, 142, 218];
const COL_W = 72;
const LABEL_X = 8;
const TABLE_LEFT = 6;
const TABLE_RIGHT = 292;
const HEADER_Y = 8;
const HEADER_H = 14;
const ROW_H = 22;
const ROW_GAP = 3;
const FIRST_ROW_Y = HEADER_Y + HEADER_H + 5;
const TABLE_BOTTOM = FIRST_ROW_Y + (ROWS.length - 1) * (ROW_H + ROW_GAP) + ROW_H;
const VIEWBOX_H = TABLE_BOTTOM + 8; // 8px breathing room to match the 8px above the header

const rowY = (i) => FIRST_ROW_Y + i * (ROW_H + ROW_GAP);

// A simplified jersey silhouette — a coloured torso with two sleeve
// notches and a collar cutout — reused for every cell, only the fill
// colour changes. Drawn on a local 10x10 grid.
const Jersey = ({ x, y, color, scale = 0.85 }) => (
  <g transform={`translate(${x},${y}) scale(${scale})`}>
    <rect x="2" y="2" width="6" height="8" rx="1" fill={color} />
    <polygon points="0,2 2,1 2,4 0,4.6" fill={color} />
    <polygon points="10,2 8,1 8,4 10,4.6" fill={color} />
    <polygon points="4,2 6,2 5,3.3" fill="#000" />
  </g>
);

const ResultIcon = ({ x, y, win }) => (
  <g transform={`translate(${x},${y})`}>
    <circle r="4" fill={win ? "#1f9d55" : "#c0392b"} />
    {win ? (
      <polyline points="-2,0 -0.5,1.6 2.2,-1.8" fill="none" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <>
        <line x1="-1.6" y1="-1.6" x2="1.6" y2="1.6" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="1.6" y1="-1.6" x2="-1.6" y2="1.6" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
      </>
    )}
  </g>
);

const Trophy = ({ x, y }) => (
  <g transform={`translate(${x},${y})`}>
    <path
      d="M-3,-3 h6 v2.5 c0,2 -1.3,3 -3,3 s-3,-1 -3,-3 Z"
      fill="#fff017"
      stroke="#000"
      strokeWidth="0.4"
    />
    <path d="M-3,-2.5 c-1.6,0 -1.6,2.6 0,2.8" fill="none" stroke="#fff017" strokeWidth="0.8" />
    <path d="M3,-2.5 c1.6,0 1.6,2.6 0,2.8" fill="none" stroke="#fff017" strokeWidth="0.8" />
    <rect x="-0.6" y="2.5" width="1.2" height="1.4" fill="#fff017" />
    <rect x="-2" y="3.9" width="4" height="1" rx="0.3" fill="#fff017" />
  </g>
);

const Cell = ({ i, rowIdx, team, win, isWinnerRow }) => {
  const cellX = COL_X[i];
  const cy = rowY(rowIdx) + ROW_H / 2;
  if (!team) {
    // Eliminated — nothing to show here. A faint dash marks it as
    // deliberately empty rather than an unrendered gap.
    return (
      <line
        x1={cellX + COL_W / 2 - 6}
        y1={cy}
        x2={cellX + COL_W / 2 + 6}
        y2={cy}
        stroke="#3a3a3a"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    );
  }
  // Text width for 3 bold caps at 9px runs wider than it looks on paper
  // (M/W especially) — checkX needs real clearance past textX or the
  // result icon overlaps the team code's last letter. Verified against a
  // 3x-DPI close-up render, not just the eye at 1x.
  const contentW = 48;
  const startX = cellX + (COL_W - contentW) / 2 - (isWinnerRow && i === 2 ? 10 : 0);
  const jerseyX = startX;
  const textX = jerseyX + 13;
  const checkX = textX + 25;
  return (
    <g>
      <Jersey x={jerseyX} y={cy - 5} color={TEAM_COLORS[team]} />
      <text x={textX} y={cy + 3} fontSize="9" fontWeight="800" fill="#fff">
        {team}
      </text>
      <ResultIcon x={checkX} y={cy} win={win} />
      {isWinnerRow && i === 2 && <Trophy x={checkX + 12} y={cy} />}
    </g>
  );
};

const SurvivalTable = () => {
  const winnerRowIdx = ROWS.findIndex((r) => r.winner);
  const winnerY = rowY(winnerRowIdx);

  return (
    <svg
      viewBox={`0 0 300 ${VIEWBOX_H}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="Entries table: 4 players across 3 rounds. Two lose early and their remaining rounds are empty. One player loses in round 3. One player wins all three rounds, is marked with a trophy, and takes the rest of the pot. The round 3 column is highlighted: players who reach it share a bonus."
    >
      {/* R3 column yellow tint — spans the whole table height */}
      <rect
        x={COL_X[2] - 4}
        y={HEADER_Y - 2}
        width={COL_W + 8}
        height={TABLE_BOTTOM - HEADER_Y + 2}
        fill="#fff017"
        fillOpacity="0.1"
      />

      {/* row banding */}
      {ROWS.map((r, i) => (
        <rect
          key={r.player}
          x={TABLE_LEFT}
          y={rowY(i)}
          width={TABLE_RIGHT - TABLE_LEFT}
          height={ROW_H}
          rx="3"
          fill={i % 2 === 0 ? "#161616" : "#101010"}
        />
      ))}

      {/* winner row outline — second-strongest emphasis */}
      <rect
        x={TABLE_LEFT}
        y={winnerY}
        width={TABLE_RIGHT - TABLE_LEFT}
        height={ROW_H}
        rx="3"
        fill="none"
        stroke="#fff017"
        strokeWidth="1.3"
        strokeOpacity="0.85"
      />

      {/* header row */}
      <text x={LABEL_X} y={HEADER_Y + 10} fontSize="8" fontWeight="700" fill="#777">
        Players
      </text>
      {["R1", "R2", "R3"].map((label, i) => (
        <text
          key={label}
          x={COL_X[i] + COL_W / 2}
          y={HEADER_Y + 10}
          textAnchor="middle"
          fontSize="9.5"
          fontWeight="800"
          fill={i === 2 ? "#fff017" : "#ccc"}
        >
          {label}
        </text>
      ))}
      <line x1={TABLE_LEFT} y1={HEADER_Y + HEADER_H} x2={TABLE_RIGHT} y2={HEADER_Y + HEADER_H} stroke="#333" strokeWidth="1" />

      {/* rows */}
      {ROWS.map((r, rowIdx) => (
        <g key={r.player}>
          <text x={LABEL_X} y={rowY(rowIdx) + ROW_H / 2 + 3} fontSize="8.5" fontWeight="700" fill="#e6e6e6">
            {r.player}
          </text>
          {r.picks.map((team, i) => (
            <Cell
              key={i}
              i={i}
              rowIdx={rowIdx}
              team={team}
              win={team ? i !== r.loseAt : false}
              isWinnerRow={!!r.winner}
            />
          ))}
        </g>
      ))}
    </svg>
  );
};

export default SurvivalTable;
